import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CaretOpenIcon, CaretCloseIcon, LabelIcon, CaretDownNewIcon, CaretUpIcon } from "../../icons";
import { getToolDisplay } from "./toolRegistry";
import { ToolStatusIndicator } from "./ToolStatusIndicator";
import { extractToolResultImageIds, resolveToolResultImagePreviews, } from "./toolResultImagePreview";
import { ToolResultImageGallery } from "./ToolResultImageGallery";
import { extractToolResultSemanticLog } from "./toolResultSemanticLog";
import { ToolSemanticLogPanel } from "./ToolSemanticLogPanel";
import { ToolInputOutputPanel, hasToolInputOutputPayload } from "./ToolInputOutputPanel";
// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Check if a string looks like an ISO date
 */
const isISODateString = (str) => {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
};
/**
 * Check if a string looks like a file path
 */
const isFilePath = (str) => {
    return /^([A-Za-z]:\\|\/|~\/|\.\.\/)/.test(str) ||
        /\.(xlsx|xls|csv|pdf|docx|pptx|txt|json|xml|py|ts|js)$/i.test(str);
};
const formatPrimitiveValue = (value) => {
    if (value === null) {
        return { display: 'null', className: 'text-gray-400 italic', icon: '∅' };
    }
    if (value === undefined) {
        return { display: 'undefined', className: 'text-gray-400 italic', icon: '∅' };
    }
    if (typeof value === 'boolean') {
        return value
            ? { display: 'true', className: 'text-emerald-600 font-medium', icon: '✓' }
            : { display: 'false', className: 'text-red-500 font-medium', icon: '✗' };
    }
    if (typeof value === 'number') {
        // Format large numbers with locale, preserve decimals
        const formatted = Number.isInteger(value)
            ? value.toLocaleString()
            : value.toLocaleString(undefined, { maximumFractionDigits: 6 });
        return { display: formatted, className: 'text-blue-600 font-mono' };
    }
    if (typeof value === 'string') {
        // Check if it's an ISO date string
        if (isISODateString(value)) {
            try {
                const date = new Date(value);
                const formatted = date.toLocaleString();
                return { display: formatted, className: 'text-violet-600', icon: '📅' };
            }
            catch { /* not a date */ }
        }
        // Check if it's a file path
        if (isFilePath(value)) {
            // Show just the filename for long paths
            const parts = value.replace(/\\\\/g, '\\').split(/[/\\]/);
            const filename = parts[parts.length - 1];
            const display = value.length > 50 ? `.../${filename}` : value;
            return { display, className: 'text-amber-700 font-mono text-[12px]', icon: '📁' };
        }
        // Regular string - show without quotes for cleaner display
        const truncated = value.length > 100 ? `${value.slice(0, 100)}...` : value;
        return { display: truncated, className: 'text-emerald-700' };
    }
    return { display: String(value), className: 'text-gray-900' };
};
/**
 * Check if a value is expandable (object or non-empty array)
 */
const isExpandableValue = (value) => {
    if (Array.isArray(value))
        return value.length > 0;
    if (value !== null && typeof value === 'object')
        return Object.keys(value).length > 0;
    return false;
};
/**
 * Convert camelCase, PascalCase, or snake_case to Title Case for display
 * Handles consecutive capitals: camelCaseABC → Camel Case ABC
 * Handles snake_case: snake_case → Snake Case
 */
const formatDisplayName = (str) => {
    // Handle array notation like "requirements[0]"
    const match = str.match(/^(.+)\[(\d+)\]$/);
    if (match) {
        return `${formatDisplayName(match[1])}[${match[2]}]`;
    }
    // Handle snake_case first
    if (str.includes('_')) {
        return str.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }
    // Handle camelCase and PascalCase with consecutive capitals
    // "camelCaseABC" → "Camel Case ABC"
    return str
        // Insert space before uppercase that follows lowercase
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        // Insert space between consecutive capitals when followed by lowercase
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        // Capitalize first letter
        .replace(/^./, s => s.toUpperCase())
        .trim();
};
const ExpandableFieldValue = ({ value, depth = 0, maxDepth = 3, defaultExpanded = true // Expanded by default!
 }) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    // For primitives, use the enhanced formatter with icons
    if (!isExpandableValue(value) || depth >= maxDepth) {
        const formatted = formatPrimitiveValue(value);
        return (_jsxs("span", { className: `text-[12px] break-all inline-flex items-center gap-1 ${formatted.className}`, children: [formatted.icon && _jsx("span", { className: "opacity-70", children: formatted.icon }), formatted.display] }));
    }
    const isArray = Array.isArray(value);
    const entries = isArray
        ? value.map((v, i) => [i, v])
        : Object.entries(value);
    // Inline compact view for small objects when collapsed
    const isSmall = entries.length <= 3 && entries.every(([_, v]) => !isExpandableValue(v));
    return (_jsxs("div", { className: "flex flex-col", children: [_jsxs("button", { onClick: () => setIsExpanded(!isExpanded), className: "inline-flex items-center gap-1.5 text-[12px] hover:bg-gray-100 rounded px-1 -ml-1 transition-colors cursor-pointer", children: [_jsx("svg", { className: `w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`, fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z", clipRule: "evenodd" }) }), _jsx("span", { className: "text-gray-500", children: isArray ? `Array(${entries.length})` : `Object` })] }), isExpanded && (_jsx("div", { className: "mt-1 ml-2 bg-white rounded border border-gray-100 overflow-hidden", children: entries.map(([key, val], idx) => {
                    const isLast = idx === entries.length - 1;
                    // Format key nicely: array indices stay as [0], object keys get Title Case
                    const keyDisplay = isArray ? `[${key}]` : formatDisplayName(String(key));
                    const hasNestedExpandable = isExpandableValue(val);
                    return (_jsxs("div", { className: `flex ${!isLast ? 'border-b border-gray-50' : ''} ${hasNestedExpandable ? 'flex-col' : 'items-center'}`, children: [_jsxs("div", { className: `flex items-start gap-2 px-2 py-1 ${hasNestedExpandable ? 'w-full' : ''}`, children: [_jsx("span", { className: "text-[12px] text-gray-500 shrink-0 min-w-[60px]", children: keyDisplay }), !hasNestedExpandable && (_jsx(ExpandableFieldValue, { value: val, depth: depth + 1, maxDepth: maxDepth, defaultExpanded: depth < 1 }))] }), hasNestedExpandable && (_jsx("div", { className: "pl-4 pb-1", children: _jsx(ExpandableFieldValue, { value: val, depth: depth + 1, maxDepth: maxDepth, defaultExpanded: depth < 1 }) }))] }, `${key}-${idx}`));
                }) }))] }));
};
// Tree line component for visual hierarchy
const TreeLine = ({ className = "" }) => {
    return _jsx("div", { className: `flex flex-col h-[30px] items-center w-[16px] shrink-0 ${className}` });
};
// Helper function to check if two values are deeply equal
const isDeepEqual = (val1, val2) => {
    if (val1 === val2)
        return true;
    if (val1 == null || val2 == null)
        return false;
    if (typeof val1 !== 'object' || typeof val2 !== 'object')
        return false;
    const keys1 = Object.keys(val1);
    const keys2 = Object.keys(val2);
    if (keys1.length !== keys2.length)
        return false;
    for (const key of keys1) {
        const v1 = val1[key];
        const v2 = val2[key];
        if (!isDeepEqual(v1, v2))
            return false;
    }
    return true;
};
// Helper function to get unique array items
const getUniqueArrayItems = (arr) => {
    const unique = [];
    for (const item of arr) {
        const isDuplicate = unique.some(existingItem => isDeepEqual(item, existingItem));
        if (!isDuplicate) {
            unique.push(item);
        }
    }
    return unique;
};
export const PropertyRow = ({ name, value, depth = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(true); // Start expanded for better UX
    const isExpandable = isExpandableValue(value);
    const isArray = Array.isArray(value);
    // Get unique items for arrays
    const arrayItems = isArray ? getUniqueArrayItems(value) : [];
    // Render spacers based on depth
    const renderSpacers = () => {
        const spacers = [];
        for (let i = 0; i < depth; i++) {
            spacers.push(_jsx(TreeLine, {}, `spacer-${i}`));
        }
        return spacers;
    };
    // Determine which icon to show for top-level fields
    const getFieldIcon = () => {
        if (depth !== 0)
            return null; // No icon for nested items
        // Only show icons for specific array fields (queries, result types, etc.)
        if (isArray) {
            const lowerName = name.toLowerCase();
            if (lowerName === 'queries' || lowerName === 'result types' || lowerName === 'slides') {
                return _jsx(LabelIcon, { className: "shrink-0" });
            }
        }
        // No icon for regular properties or objects
        return null;
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex gap-[8px] items-start py-[5px] rounded-[6px] w-full", children: [_jsxs("div", { className: "flex gap-[4px] items-start w-[180px] shrink-0", children: [_jsxs("div", { className: "flex gap-[8px] h-[20px] items-center shrink-0", children: [renderSpacers(), isExpandable ? (_jsx("button", { onClick: () => setIsExpanded(!isExpanded), className: "shrink-0 cursor-pointer", title: isExpanded ? "Collapse" : "Expand", children: isExpanded ? _jsx(CaretOpenIcon, {}) : _jsx(CaretCloseIcon, {}) })) : (depth > 0 && _jsx(TreeLine, {})), getFieldIcon()] }), _jsx("div", { className: "flex flex-1 items-start min-h-px min-w-px py-px", children: _jsx("p", { className: "flex-1 font-bold leading-normal min-h-px min-w-px text-[12px] text-gray-600 whitespace-pre-wrap", children: formatDisplayName(name) }) })] }), !isExpandable && (_jsx("div", { className: "flex flex-1 flex-col gap-[2px] items-start min-h-px min-w-px py-[3px]", children: _jsx("p", { className: "font-normal leading-[1.2] text-[12px] text-gray-900 w-full whitespace-pre-wrap", children: String(value) }) }))] }), isExpanded && isExpandable && (_jsxs("div", { className: "flex flex-col items-start w-full relative", children: [isArray ? (
                    // For arrays, render unique items with a numbered label like "# 1", "# 2"
                    arrayItems.map((item, idx) => (_jsx(PropertyRow, { name: `# ${idx + 1}`, value: item, depth: depth + 1 }, idx)))) : (
                    // For objects, render each property
                    Object.entries(value).map(([key, val]) => (_jsx(PropertyRow, { name: key, value: val, depth: depth + 1 }, key)))), isArray && arrayItems.length > 0 && (_jsx("div", { className: "absolute flex flex-col items-center w-[16px]", style: {
                            left: `${depth * 24}px`,
                            top: 0,
                            height: '100%'
                        }, children: _jsx("div", { className: "bg-gray-300 flex-1 w-px rounded-full" }) }))] }))] }));
};
/**
 * Utility function to filter out duplicate indexed fields from parameters
 * Used by both ToolCall and ToolCallCard for consistent behavior
 */
export const filterParamsForDisplay = (params) => {
    // Find all base field names that have a complete array (case-insensitive)
    const arrayFieldNames = new Map(); // lowercase -> original name
    Object.entries(params).forEach(([key, value]) => {
        // If this field is an array, mark its name
        if (Array.isArray(value)) {
            arrayFieldNames.set(key.toLowerCase(), key);
        }
    });
    // Group indexed fields into arrays (e.g., "Result Types[0]", "Result Types[1]" → "Result Types": [...])
    const grouped = {};
    const filtered = {};
    Object.entries(params).forEach(([key, value]) => {
        // Check if this field is an indexed version (e.g., "queries[0]", "Result Types[0]")
        // Trim any whitespace from the key first
        const trimmedKey = key.trim();
        const indexedMatch = trimmedKey.match(/^(.+)\[(\d+)\]$/);
        if (indexedMatch) {
            const baseName = indexedMatch[1].trim(); // Also trim the base name
            const index = parseInt(indexedMatch[2], 10);
            // If we already have the complete array, skip this indexed field
            if (arrayFieldNames.has(baseName.toLowerCase())) {
                return;
            }
            // Otherwise, group it into an array with its index
            if (!grouped[baseName]) {
                grouped[baseName] = [];
            }
            grouped[baseName].push({ index, value });
        }
        else {
            filtered[key] = value;
        }
    });
    // Merge grouped arrays into the filtered result (sorted by index)
    Object.entries(grouped).forEach(([key, items]) => {
        // Sort by index and extract values
        const sortedValues = items.sort((a, b) => a.index - b.index).map(item => item.value);
        filtered[key] = sortedValues;
    });
    return filtered;
};
const parseToolArgs = (tool) => {
    if (!tool?.args)
        return undefined;
    try {
        return JSON.parse(tool.args);
    }
    catch {
        return undefined;
    }
};
const extractUiDescription = (tool, parsedArgs) => {
    if (!tool)
        return null;
    const fieldMatch = tool.fields?.find((field) => {
        const name = field.name.toLowerCase();
        return name === "uidescription" || name === "ui_description";
    });
    if (fieldMatch && typeof fieldMatch.value === "string") {
        return fieldMatch.value;
    }
    if (parsedArgs) {
        const value = parsedArgs.uiDescription ?? parsedArgs.ui_description;
        if (typeof value === "string") {
            return value;
        }
    }
    return null;
};
export const ToolCall = ({ tool }) => {
    if (!tool)
        return null;
    const isComplete = tool.status === "complete";
    const isActive = tool.status === "streaming" || tool.status === "calling";
    const parsedArgs = parseToolArgs(tool);
    const { name, icon } = getToolDisplay(tool.name, isComplete, parsedArgs);
    const explicitUiDescription = extractUiDescription(tool, parsedArgs);
    const hasUiDescription = Boolean(explicitUiDescription && explicitUiDescription.trim().length > 0);
    const uiDescription = explicitUiDescription ?? "";
    const imageIds = useMemo(() => extractToolResultImageIds(tool.result), [tool.result]);
    const semanticLog = useMemo(() => extractToolResultSemanticLog(tool.result), [tool.result]);
    const toolInputPayload = useMemo(() => parsedArgs ?? tool.args ?? undefined, [parsedArgs, tool.args]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const hasImageResults = imagePreviews.length > 0;
    const hasSemanticLog = Boolean(semanticLog?.text);
    const hasInputOutput = useMemo(() => hasToolInputOutputPayload(toolInputPayload, tool.result), [toolInputPayload, tool.result]);
    const hasBody = hasUiDescription || hasImageResults || hasSemanticLog || hasInputOutput;
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [didAutoCollapse, setDidAutoCollapse] = useState(false);
    const showChevron = hasBody && isComplete && didAutoCollapse;
    const imageKey = imageIds.join("|");
    const toggleCollapse = useCallback(() => {
        if (!showChevron)
            return;
        setIsCollapsed((prev) => !prev);
    }, [showChevron]);
    useEffect(() => {
        setIsCollapsed(false);
        setDidAutoCollapse(false);
    }, [tool.id]);
    useEffect(() => {
        let isCancelled = false;
        if (imageIds.length === 0) {
            setImagePreviews([]);
            return undefined;
        }
        resolveToolResultImagePreviews(imageIds).then((resolved) => {
            if (!isCancelled) {
                setImagePreviews(resolved);
            }
        });
        return () => {
            isCancelled = true;
        };
    }, [imageKey, tool.id]);
    useEffect(() => {
        let timeoutId = null;
        if (hasBody && isComplete && !didAutoCollapse) {
            timeoutId = window.setTimeout(() => {
                setIsCollapsed(true);
                setDidAutoCollapse(true);
            }, 2000);
        }
        if (isActive) {
            setIsCollapsed(false);
            setDidAutoCollapse(false);
        }
        return timeoutId !== null ? () => window.clearTimeout(timeoutId) : undefined;
    }, [didAutoCollapse, hasBody, isActive, isComplete]);
    return (_jsxs("div", { className: `flex flex-col items-start relative bg-white rounded-lg overflow-hidden border border-solid border-gray-300 w-full ${isActive ? 'shadow-[0px_0px_6px_-4px_rgba(0,0,0,0.1),0px_4px_4px_0px_rgba(0,0,0,0.06),0px_0px_2px_0px_rgba(0,0,0,0.06),0px_0px_1px_0px_rgba(0,0,0,0.04)]' : ''}`, children: [_jsx("div", { className: `flex items-center pl-[6px] pr-[8px] py-[6px] w-full shrink-0 ${hasBody && !isCollapsed ? "border-b [border-bottom-style:solid] border-gray-300" : ""} ${showChevron ? "cursor-pointer" : ""}`, role: showChevron ? "button" : undefined, tabIndex: showChevron ? 0 : undefined, "aria-expanded": showChevron ? !isCollapsed : undefined, onClick: showChevron ? toggleCollapse : undefined, onKeyDown: showChevron ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleCollapse();
                    }
                } : undefined, children: _jsxs("div", { className: "flex flex-1 gap-[8px] items-center min-h-0 min-w-0", children: [_jsx("div", { className: "shrink-0 border-[1.33px] border-solid border-white rounded-[3.66px] flex flex-col items-start justify-center", children: _jsx("div", { className: "w-[16px] h-[16px] flex items-center justify-center text-gray-600", children: icon }) }), _jsx("p", { className: "flex-1 min-h-0 min-w-0 font-bold leading-[1.5] text-[10px] text-gray-600 whitespace-pre-wrap", children: name }), showChevron ? (_jsx("button", { type: "button", className: "inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-[3px] hover:bg-gray-100", "aria-label": isCollapsed ? "Expand tool details" : "Collapse tool details", onClick: (event) => {
                                event.stopPropagation();
                                toggleCollapse();
                            }, children: isCollapsed ? (_jsx(CaretDownNewIcon, { className: "w-[12px] h-[12px]" })) : (_jsx(CaretUpIcon, { className: "w-[12px] h-[12px]" })) })) : (_jsx(ToolStatusIndicator, { status: tool.status }))] }) }), hasBody && !isCollapsed && (_jsxs("div", { className: "flex items-start pl-[6px] pr-[8px] py-[4px] w-full shrink-0 bg-gray-100", children: [_jsxs("div", { className: "flex flex-col items-center justify-center self-stretch shrink-0 w-[16px]", "aria-hidden": "true", children: [_jsx("div", { className: "h-px shrink-0 w-[16px]" }), _jsx("div", { className: "flex-1 min-h-0 min-w-0 bg-gray-300 w-px rounded-[22px]" }), _jsx("div", { className: "h-px shrink-0 w-[16px]" })] }), _jsxs("div", { className: "flex flex-1 flex-col items-start min-h-0 min-w-0", children: [hasUiDescription && (_jsx("div", { className: "flex items-start py-[1px] w-full rounded-[6px]", children: _jsx("div", { className: "flex flex-1 items-center min-h-0 min-w-0", children: _jsx("p", { className: "flex-1 min-h-0 min-w-0 font-normal leading-[1.5] text-[10px] text-gray-900 whitespace-pre-wrap", children: uiDescription }) }) })), hasImageResults && (_jsx(ToolResultImageGallery, { toolId: tool.id, images: imagePreviews, className: `flex flex-wrap items-start gap-2 w-full ${hasUiDescription ? "pt-[4px]" : "py-[1px]"}` })), hasSemanticLog && semanticLog && (_jsx(ToolSemanticLogPanel, { text: semanticLog.text, entryCount: semanticLog.entryCount, className: `w-full ${hasUiDescription || hasImageResults ? "pt-[4px]" : "py-[1px]"}` })), hasInputOutput && (_jsx(ToolInputOutputPanel, { input: toolInputPayload, output: tool.result, className: `w-full ${hasUiDescription || hasImageResults || hasSemanticLog ? "pt-[4px]" : "py-[1px]"}` }))] })] }))] }));
};
